import axios from 'axios';
import * as Sentry from "@sentry/react";

/**
 * Sanitizes a string to prevent XSS and other vulnerabilities
 * Also normalizes spaces: removes spaces at the beginning and end,
 * and reduces multiple spaces between words to just one
 */
function sanitizeString(value) {
  if (!value) return value;
  
  return value
    .replace(/<[^>]*>/g, "")
    .replace(/javascript:/gi, "")
    .replace(/data:/gi, "")
    .replace(/\son\w+\s*=\s*["']?[^"']*["']?/gi, "")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Recursively sanitizes all string values in an object or array
 */
function sanitizeObject(data) {
  if (data === null || data === undefined) return data;
  if (typeof data === "string") return sanitizeString(data);
  if (data instanceof File || data instanceof Blob) return data;
  
  if (Array.isArray(data)) {
    return data.map(sanitizeObject);
  }
  
  if (typeof data === "object") {
    return Object.entries(data).reduce((result, [key, value]) => {
      result[key] = sanitizeObject(value);
      return result;
    }, {});
  }
  
  return data;
}

/**
 * Sanitizes a FormData object
 */
function sanitizeFormData(formData) {
  const sanitizedFormData = new FormData();
  
  for (const [key, value] of formData.entries()) {
    sanitizedFormData.append(
      key, 
      typeof value === "string" ? sanitizeString(value) : value
    );
  }
  
  return sanitizedFormData;
}

// Request interceptor - sanitizes data before sending to server
axios.interceptors.request.use(
  config => {
    if (config.data) {
      if (config.data instanceof FormData) {
        config.data = sanitizeFormData(config.data);
      } 
      else if (typeof config.data === "object") {
        config.data = sanitizeObject(config.data);
      } 
      else if (typeof config.data === "string") {
        try {
          const params = new URLSearchParams(config.data);
          const sanitizedParams = new URLSearchParams();
          
          for (const [key, value] of params.entries()) {
            sanitizedParams.append(key, sanitizeString(value));
          }
          
          config.data = sanitizedParams.toString();
        } catch (e) {
          config.data = sanitizeString(config.data);
        }
      } 
      else if (typeof config.data.toString === "function") {
        config.data = sanitizeString(config.data.toString());
      }
    }
    
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor - only for capturing errors
axios.interceptors.response.use(
  response => response,
  error => {
    Sentry.captureException(error);
    return Promise.reject(error);
  }
);